/**
 * The catalogue context, assembled from the store — the reactive twin of `catalog/index`'s
 * imperative `catalogContext()`.
 *
 * 🔴 **It was `usePosCatalogContext`, inside `./usePosCatalogItems`.** Neither the name nor the
 * home survived scrutiny: `recommendations` uses it, and `recommendations` is what draws the
 * KIOSK's suggestion rail — so "Pos" named the first caller, not the thing. Its neighbour is a
 * legacy card hook due for deletion, and this is not going anywhere.
 *
 * Reads through `../selectors`, the domain's one place for reducer keys.
 *
 * ⚠️ ONE deliberate exception: the channel is taken RAW from `saleDataStore.selectedChannelData`
 * where `selectChannel` normalises a channel with no `channelId` to `undefined`. Both produce the
 * same answer downstream, and taking it raw keeps the legacy POS path byte-identical to what it
 * did before the `catalog/` retirement.
 */
import { useMemo } from "react";
import { useSelector } from "react-redux";

import { selectBranchZoneId } from "../../time/branchClock";
import type { CatalogContextInput } from "../context";
import {
  selectBranchId,
  selectItemSchedules,
  selectItemsById,
  selectSoldOutItemIds,
} from "../selectors";

export function useCatalogContext(): CatalogContextInput {
  const itemsData = useSelector(selectItemsById);
  const itemSchedules = useSelector(selectItemSchedules);
  const branchTimeZoneId = useSelector(selectBranchZoneId);
  const branchId = useSelector(selectBranchId);
  const selectedChannel = useSelector((state: any) => state.saleDataStore?.selectedChannelData);
  const soldOutItemsList = useSelector(selectSoldOutItemIds);

  return useMemo(
    () => ({
      itemsData,
      itemSchedules,
      branchTimeZoneId,
      branchId,
      selectedChannel,
      soldOutItemsList,
    }),
    [itemsData, itemSchedules, branchTimeZoneId, branchId, selectedChannel, soldOutItemsList],
  );
}

export default useCatalogContext;
